var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import defer from '../utils/defer';
import { DATASTRATEGY } from '../constants';
/**
 * @ngdoc service
 * @name proximus.core.entity:RemoteEntityStore
 * @description
 * # RemoteEntityStore
 * Executes http requests to micro services for entities.
 */
export default class RemoteEntityStore {
    constructor(headerService, configurationService, networkService) {
        this.headerService = headerService;
        this.configurationService = configurationService;
        this.networkService = networkService;
        this.requestsMap = new Map();
    }
    /**
     * retrieves entity value from backend
     * @memberof proximus.core.entity:RemoteEntityStore
     * @param {Entity} entity to get value for
     * @returns {Promise} promise which get resolved with entity
     */
    getEntity(entity) {
        return this.withRetry(entity, 'GET');
    }
    /**
     * checks entity meta-information from backend
     * @memberof proximus.core.entity:RemoteEntityStore
     * @param {Entity} entity to get value for
     * @returns {Promise} promise which get resolved with entity
     */
    headEntity(entity) {
        return this.withRetry(entity, 'HEAD');
    }
    /**
     * saves entity value to backend and assigns generated id attribute
     * @memberof proximus.core.entity:RemoteEntityStore
     * @param {Entity} entity to get value for
     * @returns {Promise} promise which get resolved with entity
     */
    saveEntity(entity) {
        return this.withRetry(entity, 'POST');
    }
    /**
     * updates entity value to backend
     * @memberof proximus.core.entity:RemoteEntityStore
     * @param {Entity} entity to get value for
     * @returns {Promise} promise which get resolved with entity
     */
    updateEntity(entity) {
        return this.withRetry(entity, 'PUT');
    }
    /**
     * deletes entity value from backend
     * @memberof proximus.core.entity:RemoteEntityStore
     * @param {entity} entity to get value for
     * @returns {Promise} promise which get resolved with entity
     */
    deleteEntity(entity) {
        return this.withRetry(entity, 'DELETE');
    }
    /**
     * cancel entity value from back-end
     * @memberOf proximus.core.entity:RemoteEntityStore
     * @param {entity} entity to get the value
     * @returns {Promise} in witch get resolved
     */
    cancelEntityRequest(entity) {
        const deferred = defer();
        const xhr = this.requestsMap.get(entity.uniqueKey);
        if (!xhr) {
            deferred.reject();
        }
        else {
            xhr.abort();
            this.requestsMap.delete(entity.uniqueKey);
            deferred.resolve();
        }
        return deferred.promise;
    }
    //////////////////
    // PRIVATES
    //////////////////
    processEntityRequestIfOnline(entity, method) {
        const deferred = defer();
        this.networkService
            .isOnline()
            .then(isConnected => {
            if (!isConnected &&
                (entity.dataStrategy === DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS ||
                    entity.dataStrategy === DATASTRATEGY.PROXY_STORAGE)) {
                entity.errorData = {
                    code: 'OFFLINE',
                    message: 'The network is offline',
                };
                this.configurationService.onErrorCallback(entity);
                deferred.reject(entity);
            }
            this.createHttpRequest(method, entity)
                .then(request => {
                this.executeHttpRequest(deferred, request, entity);
            })
                .catch(error => {
                entity.errorData = {
                    message: 'Could not create http request for entity',
                };
                this.configurationService.onErrorCallback(entity);
                deferred.reject(entity);
            });
        })
            .catch(error => deferred.reject(error));
        return deferred.promise;
    }
    executeHttpRequest(deferred, request, entity) {
        const xhr = new XMLHttpRequest();
        this.requestsMap.set(entity.uniqueKey, xhr);
        if (request.responseType) {
            xhr.responseType = request.responseType;
        }
        xhr.open(request.method, entity.externalURL ? request.url : this.configurationService.getBaseURL() + request.url, true);
        Object.keys(request.headers).forEach((headerName) => {
            xhr.setRequestHeader(headerName, request.headers[headerName]);
        });
        const timeout = (xhr.timeout =
            entity.timeout || this.configurationService.getRequestTimeout() || 10000);
        const ontimeout = () => {
            // NOTE: XMLHttpRequest ontimeout is a ProgressEvent so the error has no message
            entity.errorData = {
                message: `Timeout after ${entity.timeout}`,
            };
            this.configurationService.onErrorCallback(entity);
            this.requestsMap.delete(entity);
            deferred.reject(entity);
        };
        if (entity.emulateTimeout) {
            setTimeout(() => {
                if (xhr.status === 0) {
                    ontimeout();
                    xhr.abort();
                    this.requestsMap.delete(entity);
                }
            }, timeout);
        }
        else {
            xhr.timeout = timeout;
        }
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 0) {
                    return deferred.reject(entity);
                }
                const headers = this.getResponseHeaders(xhr);
                entity.status = xhr.status;
                this.headerService.setEntityPropertiesFromHeaders(entity, headers);
                if (xhr.status >= 200 && xhr.status <= 299) {
                    // we set fetchedOn only in success because expiresIn should be calculated on success response
                    entity.fetchedOn = Date.now();
                    entity.fromStorage = false;
                    this.requestsMap.delete(entity);
                    try {
                        // Mimicking the old code
                        if (xhr.responseType) {
                            entity.value = xhr.response;
                        }
                        else {
                            entity.value = xhr.responseText ? JSON.parse(xhr.responseText) : xhr.responseText;
                        }
                        deferred.resolve(entity);
                    }
                    catch (error) {
                        entity.errorData = {
                            message: error.message,
                        };
                        this.configurationService.onErrorCallback(entity);
                        deferred.reject(entity);
                    }
                }
                else if (xhr.status === 304) {
                    entity.fetchedOn = Date.now();
                    deferred.resolve(entity);
                    this.requestsMap.delete(entity);
                }
                else {
                    try {
                        // cf. https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseText#exceptions
                        entity.errorData = JSON.parse(xhr.responseType === 'text' || xhr.responseType === ''
                            ? xhr.responseText
                            : xhr.response);
                        if (!entity.errorData.message) {
                            entity.errorData.message = 'µService has returned a ' + entity.status;
                        }
                    }
                    catch (e) {
                        entity.errorData = {
                            message: xhr.responseType === 'text' || xhr.responseType === ''
                                ? xhr.responseText
                                : xhr.response,
                        };
                    }
                    finally {
                        this.configurationService.onErrorCallback(entity);
                    }
                    deferred.reject(entity);
                }
            }
        };
        //HACK: ProgressEvent is not enough for us, see https://github.com/Microsoft/TypeScript/issues/21414
        xhr.onerror = (error) => {
            try {
                entity.errorData = JSON.parse(error.message);
                if (!entity.errorData.message) {
                    entity.errorData.message = 'µService has returned a ' + entity.status;
                }
            }
            catch (e) {
                entity.errorData = {
                    message: error.message,
                };
            }
            finally {
                this.configurationService.onErrorCallback(entity);
            }
            deferred.reject(entity);
        };
        xhr.ontimeout = ontimeout;
        xhr._meta = this.addMetaInformation(request, entity);
        xhr.send(request.body);
    }
    withRetry(entity, operation) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!entity.retry || typeof entity.retry !== 'number') {
                entity.retry = 1;
            }
            let i = 0;
            let result;
            let error;
            while (i < entity.retry) {
                try {
                    result = yield this.processEntityRequestIfOnline(entity, operation);
                    break;
                }
                catch (e) {
                    error = e;
                    i++;
                    if (i < entity.retry) {
                        entity.errorData = null;
                        entity.etag = null;
                        entity.expires = null;
                    }
                }
            }
            if (error && i === entity.retry) {
                throw error;
            }
            return result;
        });
    }
    createHttpRequest(methodName, entity) {
        const deferred = defer();
        this.headerService.getRequestHeaders(entity).then(headers => {
            const httpRequest = {
                method: methodName,
                url: entity.url,
                headers: headers,
                cache: undefined,
                body: undefined,
                responseType: undefined,
            };
            if (entity.disableCache) {
                // Check if this is the first parameter
                const urlParams = httpRequest.url.split('?');
                if (urlParams.length === 1) {
                    httpRequest.url += '?ts=' + new Date().getTime();
                }
                else {
                    httpRequest.url += '&ts=' + new Date().getTime();
                }
            }
            if ('GET' !== methodName && 'HEAD' !== methodName) {
                httpRequest.body =
                    entity.value instanceof FormData || typeof entity.value !== 'object'
                        ? entity.value
                        : JSON.stringify(entity.value);
            }
            // For list of available values check https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType
            if (entity.responseType) {
                httpRequest.responseType = entity.responseType;
            }
            deferred.resolve(httpRequest);
        });
        return deferred.promise;
    }
    getResponseHeaders(xhr) {
        const headerString = xhr.getAllResponseHeaders();
        if (!headerString) {
            return {};
        }
        const responseHeaders = headerString.trim().split('\n');
        const headers = {};
        responseHeaders.forEach(row => {
            const index = row.indexOf(':');
            const key = row.slice(0, index).toLowerCase().trim();
            const value = row.slice(index + 1).trim();
            headers[key] = value;
        });
        return headers;
    }
    addMetaInformation(request, entity) {
        return Object.assign({ _url: request.url, _method: request.method, _headers: request.headers, _body: request.body }, entity);
    }
}
